import * as React from 'react';
import clsx from 'clsx';
import Icons from '@/const/icons';
import { Spinner } from '@/components/common';
import { DocumentationCardProps } from '@/interfaces';
import { useDocumentationModals } from '@/hooks/useDocumentationModals';
import { isCaseCardDisabled } from '@/utils/caseStatus';
import { DocumentationModals } from '../documentation/DocumentationModals';
import { CaseDetailsCard } from './CaseDetailsCard';
import { CaseDetailsEmptyState } from './CaseDetailsEmptyState';
import { CaseDetailsErrorState } from './CaseDetailsErrorState';
import { CasePill } from './CasePill';
import { DocumentRow } from './DocumentRow';

export const DocumentationCard: React.FC<DocumentationCardProps> = ({
	applicationId,
	caseName,
	status,
	stepName,
	filesApplications,
	clientApproveEmail,
	clientFirstname,
	clientLastname,
	isClientView = false,
	className,
	isError = false,
	refetch,
	onRetry,
}) => {
	const modals = useDocumentationModals({ applicationId, refetch });
	const isDeclined = status === 'Declined';
	const filesCount = filesApplications?.length ?? 0;

	return (
		<>
			<CaseDetailsCard
				title="Documentation"
				pill={
					filesApplications ? (
						<CasePill tone="neutral">
							{filesCount} {filesCount === 1 ? 'file' : 'files'}
						</CasePill>
					) : undefined
				}
				disabled={
					!!isCaseCardDisabled({ status, stepName, clientApproveEmail })
				}
				className={clsx('lg:absolute lg:inset-0', className)}
				classNameHeader="lg:!px-6 lg:!pt-6"
				classNameBody="lg:!px-6 lg:!pb-6"
			>
				{!filesApplications && isError && onRetry ? (
					<CaseDetailsErrorState
						className="flex-1"
						message="We couldn’t load your documents. Please try again."
						onRetry={onRetry}
					/>
				) : !filesApplications ? (
					<div className="flex w-full flex-1 items-center justify-center py-10">
						<Spinner type="loadingPage" />
					</div>
				) : filesCount > 0 ? (
					<ul
						role="list"
						tabIndex={0}
						aria-label="Case documents"
						className="scroll-custom mt-2 max-h-96 min-h-0 flex-1 overflow-y-auto rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary lg:max-h-none"
					>
						{filesApplications.map((item, index) => (
							<DocumentRow
								key={'document-' + index}
								item={item}
								isDeclined={isDeclined}
								onClickModal={modals.handleOnClickModal}
							/>
						))}
					</ul>
				) : (
					<CaseDetailsEmptyState
						className="mt-[18px] flex-1 !px-[18px] lg:mt-4 lg:!px-4"
						icon={Icons.book}
						iconClassName="text-gray-35"
						iconSize="sm"
						titleType="semibold-14"
						title="No documents yet"
						text="Documents related to your case will appear here as soon as they’re available."
					/>
				)}
			</CaseDetailsCard>
			<DocumentationModals
				modals={modals}
				caseName={caseName}
				applicationId={applicationId}
				uploadButton={false}
				clientFirstname={clientFirstname}
				clientLastname={clientLastname}
				isClientView={isClientView}
				refetch={refetch}
			/>
		</>
	);
};
